/**
 * Pros YouTube Videos Slider and Modal
 * Handles video playback and slider functionality
 */

class ProsYoutubeVideos {
  constructor(container) {
    this.container = container;
    this.videoSlider = this.container.querySelector('.pros-youtube-videos__wrapper');
    this.videoItems = this.container.querySelectorAll('.pros-youtube-videos__item');
    this.videoButtons = this.container.querySelectorAll('.pros-youtube-videos__play-button');
    this.prevButton = this.container.querySelector('.youtube-channel-videos__nav-button--prev');
    this.nextButton = this.container.querySelector('.youtube-channel-videos__nav-button--next');
    this.modal = document.getElementById('ProsYoutubeModal');
    this.modalClose = this.modal.querySelector('.pros-youtube-modal__close');
    this.iframe = document.getElementById('ProsYoutubeIframe');
    
    // Settings
    this.itemWidth = this.videoItems.length > 0 ? this.videoItems[0].offsetWidth : 0;
    this.autoplayEnabled = this.container.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.container.dataset.autoplaySpeed || 5) * 1000;
    this.autoplayInterval = null;
    
    this.init();
  }

  init() {
    // Initialize event listeners
    this.initPlayButtons();
    this.initModalEvents();
    this.initSliderNavigation();
    
    // Start autoplay if enabled
    if (this.autoplayEnabled) {
      this.startAutoplay();
      this.initAutoplayPause();
    }
  }

  initPlayButtons() {
    // Add click event to play buttons
    this.videoButtons.forEach(button => {
      button.addEventListener('click', e => {
        e.preventDefault();
        const videoId = button.dataset.videoId;
        if (videoId) {
          this.openVideoModal(videoId);
        }
      });
    });
  }

  initModalEvents() {
    // Close modal when clicking close button
    this.modalClose.addEventListener('click', () => {
      this.closeVideoModal();
    });
    
    // Close modal when clicking outside the video
    this.modal.addEventListener('click', e => {
      if (e.target === this.modal) {
        this.closeVideoModal();
      }
    });
    
    // Close modal on escape key press
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.isModalOpen()) {
        this.closeVideoModal();
      }
    });
  }

  initSliderNavigation() {
    // If navigation buttons exist, add click events
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => {
        this.navigate(-1);
      });
    }
    
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => {
        this.navigate(1);
      });
    }
    
    // Add touch/swipe handling
    this.initTouchNavigation();
  }

  initTouchNavigation() {
    let startX, startY, dist, threshold = 150; // Minimum distance for swipe
    let allowedTime = 300; // Maximum time for swipe
    let elapsedTime, startTime;
    
    this.videoSlider.addEventListener('touchstart', e => {
      const touchobj = e.changedTouches[0];
      startX = touchobj.pageX;
      startY = touchobj.pageY;
      startTime = new Date().getTime(); // Record time when finger first makes contact
    }, { passive: true });
    
    this.videoSlider.addEventListener('touchend', e => {
      const touchobj = e.changedTouches[0];
      dist = touchobj.pageX - startX; // Get horizontal dist traveled
      elapsedTime = new Date().getTime() - startTime; // Get time elapsed
      
      // Check that elapsed time is within specified range and horizontal distance is significant
      // and vertical distance is small enough to be considered a horizontal swipe
      if (elapsedTime <= allowedTime) {
        if (Math.abs(dist) >= threshold && Math.abs(touchobj.pageY - startY) <= 100) {
          this.navigate(dist < 0 ? 1 : -1);
        }
      }
    }, { passive: true });
  }

  navigate(direction) {
    // Calculate the new scroll position
    const scrollAmount = direction * (this.itemWidth + 30); // Item width + margin
    this.videoSlider.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
    
    // Reset autoplay timer if enabled
    if (this.autoplayEnabled) {
      this.resetAutoplay();
    }
  }

  startAutoplay() {
    this.autoplayInterval = setInterval(() => {
      this.navigate(1); // Always move forward in autoplay
    }, this.autoplaySpeed);
  }

  resetAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.startAutoplay();
    }
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  initAutoplayPause() {
    // Pause autoplay when mouse enters the slider
    this.videoSlider.addEventListener('mouseenter', () => {
      this.stopAutoplay();
    });
    
    // Resume autoplay when mouse leaves the slider
    this.videoSlider.addEventListener('mouseleave', () => {
      if (this.autoplayEnabled) {
        this.startAutoplay();
      }
    });
    
    // Pause autoplay when touching the slider (for mobile)
    this.videoSlider.addEventListener('touchstart', () => {
      this.stopAutoplay();
    }, { passive: true });
    
    // Resume autoplay when touch ends (for mobile)
    this.videoSlider.addEventListener('touchend', () => {
      if (this.autoplayEnabled) {
        this.startAutoplay();
      }
    }, { passive: true });
  }

  openVideoModal(videoId) {
    // Set the video source with autoplay enabled
    this.iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
    
    // Show the modal
    this.modal.style.display = 'block';
    document.body.style.overflow = 'hidden'; // Prevent scrolling
    
    // Pause autoplay while video is playing
    if (this.autoplayEnabled) {
      this.stopAutoplay();
    }
  }

  closeVideoModal() {
    // Stop video playback by removing the src
    this.iframe.src = '';
    
    // Hide the modal
    this.modal.style.display = 'none';
    document.body.style.overflow = ''; // Restore scrolling
    
    // Resume autoplay if enabled
    if (this.autoplayEnabled) {
      this.startAutoplay();
    }
  }

  isModalOpen() {
    return this.modal.style.display === 'block';
  }
}

// Initialize all Pros YouTube Videos sections on the page
document.addEventListener('DOMContentLoaded', () => {
  const prosYoutubeContainers = document.querySelectorAll('.pros-youtube-videos');
  
  prosYoutubeContainers.forEach(container => {
    // Set data attributes for settings
    container.dataset.autoplay = container.querySelector('[data-autoplay]')?.dataset.autoplay || 'false';
    container.dataset.autoplaySpeed = container.querySelector('[data-autoplay-speed]')?.dataset.autoplaySpeed || '5';
    
    // Initialize the section
    new ProsYoutubeVideos(container);
  });
}); 